import type { CaseStudy } from '../types/caseStudy'

export const caseStudies: CaseStudy[] = [
  {
    id: 'snohetta',
    company: 'Snøhetta',
    logo: 'https://cdn.magicpatterns.com/uploads/qt8nWDJv3BJa647m1LENZT/image.png',
    label: 'Executive Team · Architecture & Design',
    headline: 'From 0% Regular AI Use to Executives Coaching Colleagues',
    summary:
      "Snøhetta's leadership had no AI foundation and no strategy. None of the executives were using AI regularly. After a five-session program built around each leader's actual work, executives were coaching colleagues unprompted, the CFO was teaching staff directly, and the firm stood up an AI steering group to carry the work forward.",
    featuredQuote: {
      quote:
        "We knew AI was going to change how we worked, but we didn't have a foundation or a strategy for it. Working with Michael helped us develop both in a thoughtful and meaningful way. He quickly established rapport with our diverse team, and he brought a hands-on approach that got even our most resistant team members engaging with AI productively. The work established a foundation that the firm is able to continue building on.",
      author: 'Kate Grimes',
      role: 'Former Director, Snøhetta',
    },
    sections: [
      {
        heading: 'The challenge',
        paragraphs: [
          "Snøhetta's leadership knew AI was going to change how a design studio works, but there was no foundation and no strategy. A Phase 0 assessment made the gap concrete: none of the executives were using AI regularly. In a firm built on deep expertise, asking senior leaders to become beginners at something new was the real barrier, not the technology.",
        ],
      },
      {
        heading: 'The approach',
        paragraphs: [
          "A five-session executive foundations program, built around each leader's actual work rather than generic demos. Every session started with a hands-on win on a real task, then moved to the harder questions: how much to verify, when to trust the output, and what changes for the studio.",
        ],
      },
      {
        heading: 'What happened',
        paragraphs: [
          'The first session set the tone. One executive turned her board-retreat notes into draft action items in about five minutes. She rated the result "a solid B." That clear-eyed read of what still needed her judgment carried through the rest of the program. By the third session, leaders were bringing their own problems, including a construction-claims review with more than 300 line items.',
          'By the fourth session, advocacy was outrunning the program. Without being asked, executives were coaching colleagues on using AI for board reports, the CFO was teaching staff directly, and new initiatives were starting outside the program. By the fifth, the conversation had moved from whether to use AI to how. The firm stood up an AI steering group to carry the work forward.',
        ],
      },
    ],
    additionalTestimonials: [],
  },
  {
    id: 'htec',
    company: 'HTEC Group',
    logo: 'https://cdn.magicpatterns.com/uploads/diyFRn9ySMDaoZwXUKcrWG/HTEC_Logotype_black-1.png',
    label: 'Technology Services',
    headline: 'From Skepticism to a Culture of AI Experimentation',
    summary:
      'Several senior technical members were actively skeptical that AI could add value. After a six-week program delivered to two teams, members were building AI-powered prototypes and incorporating AI into client engagements. Seventeen months later, new hires were still onboarding with program recordings and the Head of Product was advocating for company-wide expansion.',
    featuredQuote: {
      quote:
        "I think it was the best thing we've ever invested in... if it wasn't for that, they wouldn't be where they are and I would still be banging my head against a brick wall.",
      author: 'Pali Dhanoya',
      role: 'Head of Product, HTEC Group',
    },
    sections: [
      {
        heading: 'The challenge',
        paragraphs: [
          "HTEC's team wasn't just unaligned on AI. Several senior technical members were actively skeptical that AI could add any value to their work. On top of that, the company itself had experienced a prior incident that created significant wariness around AI tools across the organization.",
          'The Head of Product could see the potential but had no shared framework for how to move the team forward. What began as a conversation about training one team expanded to two once the scope of the need became clear.',
        ],
      },
      {
        heading: 'The approach',
        paragraphs: [
          "A six-week program built around HTEC's actual work, delivered separately to two teams. Two-hour sessions each week gave each team time to learn, apply, and come back with real questions. We identified AI champions on each team to sustain momentum after the program ended.",
        ],
      },
      {
        heading: 'What happened',
        paragraphs: [
          'The team went from scattered, skeptical experimentation to building AI into how they actually work. Team members started creating clickable AI-powered prototypes. They built AI into client engagements.',
          'The program created what Pali called "a culture of AI experimentation." Initiatives kept emerging on their own, long after the formal training ended.',
          'New hires were onboarding with program recordings 10 months later. The Head of Product was advocating for company-wide expansion of the training.',
          'Seventeen months after the program, HTEC\'s VP of Product and Design put it this way: "For my teams... it\'s a very noticeable difference from when we brought you in... It\'s not that it\'s all been figured out, but it is a very dramatic change in terms of ways of working."',
        ],
      },
    ],
    additionalTestimonials: [
      {
        quote:
          'My confidence in discussing AI with stakeholders has grown significantly, and I now have both the technical understanding and strategic perspective to identify real business opportunities.',
        author: 'Danijela Nestorovic',
        role: 'Principal Product Manager, HTEC Group',
      },
      {
        quote:
          "Unlike previous training efforts that weren't applicable to daily work, Michael's approach was practical and meaningful. Our product teams are now much more curious about AI and it's become part of their regular workflow.",
        author: 'Carsten Wierwille',
        role: 'VP of Product and Design, HTEC Group',
      },
    ],
  },
  {
    id: 'motorola',
    company: 'Motorola Solutions',
    logo: 'https://cdn.magicpatterns.com/uploads/3APyYKvhRHDFEwKJoXnrxg/image.png',
    label: 'Public Safety Technology',
    headline: '30 Minutes to Several Hours Saved Per Person Within 30 Days',
    summary:
      'Two product teams went through a shorter workshop format. Within their first month, team members reported meaningful time savings. One built an integrated Slack-Jira-AI workflow automating ticket creation. Another encoded usability testing expertise into a shared AI tool for the entire design team.',
    sections: [
      {
        heading: 'What happened',
        paragraphs: [
          'Two product teams. Even in a shorter workshop format, team members reported saving 30 minutes to several hours per person per week within their first month.',
        ],
        highlights: [
          'One team member built an integrated workflow connecting Slack, Jira, and AI that automated ticket creation from conversations.',
          'Another began encoding usability testing expertise into a shared AI tool so every designer on the team could follow the same process.',
        ],
      },
    ],
    additionalTestimonials: [],
  },
]
